import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  DataTable,
  DataTableColumnHeader,
  DateWithDelta,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useMemo, useState } from "react";
import {
  RiFilter3Line,
  RiListCheck,
  RiPriceTagLine,
  RiShieldLine,
} from "react-icons/ri";
import { z } from "zod";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { RiskRatingPill } from "~/components/TransactionMonitoring/RiskRatingPill";
import { RulePill } from "~/components/TransactionMonitoring/RulePill";
import { graphql } from "~/gql/client";
import {
  transactionMonitoringFindingStatusValues,
  transactionMonitoringRiskRatingValues,
} from "~/gql/client/enumValues";
import type {
  AdminTransactionMonitoringFindingRowFragment,
  TransactionMonitoringFindingFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const transactionMonitoringRuleValues = [
  "TM-001",
  "TM-002",
  "TM-003",
  "TM-004",
  "TM-005",
  "TM-006",
  "TM-007",
  "TM-008",
  "TM-009",
] as const;

const adminTransactionMonitoringFindingsDocument = graphql(`
  query AdminTransactionMonitoringFindings($after: String, $first: Int!, $filter: TransactionMonitoringFindingFilter!) {
    transactionMonitoringFindings(after: $after, first: $first, filter: $filter) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      transactionMonitoringFindings {
        ...AdminTransactionMonitoringFindingRow
      }
    }
  }

  fragment AdminTransactionMonitoringFindingRow on TransactionMonitoringFinding {
    id
    shortRef
    organisation {
      ...OrganisationCardFields
    }
    detectedAt
    rule {
      id
      label
      description
      threshold
    }
    riskRating
    status
  }
`);

const findingsFilterSchema = z.object({
  riskRatings: z
    .array(z.enum(transactionMonitoringRiskRatingValues))
    .optional()
    .nullable(),
  rules: z.array(z.enum(transactionMonitoringRuleValues)).optional().nullable(),
  statuses: z
    .array(z.enum(transactionMonitoringFindingStatusValues))
    .optional()
    .nullable(),
});

export const Route = createFileRoute("/__auth/transaction-monitoring/findings")(
  {
    head: () => opsHead("Transaction Monitoring Findings"),
    component: () => (
      <Suspense fallback={<PrimaryFullScreenSpinner />}>
        <View />
      </Suspense>
    ),
  },
);

const columns: ColumnDef<AdminTransactionMonitoringFindingRowFragment>[] = [
  {
    id: "shortRef",
    accessorFn: (row) => row.shortRef,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Finding" />
    ),
    cell: ({ row }) => (
      <div className="font-mono text-sm">{row.original.shortRef}</div>
    ),
    enableSorting: false,
  },
  {
    id: "organisation",
    accessorFn: (row) => row.organisation?.name,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) =>
      row.original.organisation ? (
        <OrganisationCard
          organisation={row.original.organisation}
          linkTo={`/organisations/${row.original.organisation.id}`}
          labelClassName="text-sm"
        />
      ) : (
        "-"
      ),
    enableSorting: false,
  },
  {
    id: "detectedAt",
    accessorFn: (row) => row.detectedAt,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Detected" />
    ),
    cell: ({ row }) => <DateWithDelta date={row.original.detectedAt} />,
    enableSorting: false,
  },
  {
    id: "rule",
    accessorFn: (row) => row.rule.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Rule" />
    ),
    cell: ({ row }) => <RulePill ruleId={row.original.rule.id} />,
    enableSorting: false,
  },
  {
    id: "label",
    accessorFn: (row) => row.rule.label,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Label" />
    ),
    cell: ({ row }) => (
      <div className="max-w-md truncate" title={row.original.rule.label}>
        {row.original.rule.label}
      </div>
    ),
    enableSorting: false,
  },
  {
    id: "riskRating",
    accessorFn: (row) => row.riskRating,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Risk" />
    ),
    cell: ({ row }) => <RiskRatingPill riskRating={row.original.riskRating} />,
    enableSorting: false,
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <StatusUI status={row.original.status} />,
    enableSorting: false,
  },
];

function View() {
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: findingsFilterSchema,
    defaultState: {
      statuses: ["OPEN"],
    },
  });

  const baseInput: {
    filter: TransactionMonitoringFindingFilter;
    first: number;
  } = {
    first: 20,
    filter,
  };

  const { data, fetchMore, loading, networkStatus } = useQuery(
    adminTransactionMonitoringFindingsDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const findings =
    data?.transactionMonitoringFindings?.transactionMonitoringFindings ?? [];
  const nextCursor = data?.transactionMonitoringFindings?.pageInfo.hasNextPage
    ? (data.transactionMonitoringFindings.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <div>
      <FindingsFilterBar
        filterValue={filter}
        onFilterChange={setFilter}
        totalCount={data?.transactionMonitoringFindings?.totalCount ?? 0}
      />
      <DataTable
        data={findings}
        columns={columns}
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        manualPagination={true}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border"
        onRowClick={(row) => {
          void navigate({
            to: "/transaction-monitoring/findings/$findingId",
            params: { findingId: row.original.id },
          });
        }}
      />
      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!findings.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </div>
  );
}

function FindingsFilterBar({
  filterValue,
  onFilterChange,
  totalCount,
}: {
  filterValue: z.infer<typeof findingsFilterSchema>;
  onFilterChange: (_: z.infer<typeof findingsFilterSchema>) => void;
  totalCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("status");

  const statusOptions = useMemo(
    () =>
      transactionMonitoringFindingStatusValues.map((value) => ({
        value,
        label: prettyPrintEnum(value),
      })),
    [],
  );

  const riskRatingOptions = useMemo(
    () =>
      transactionMonitoringRiskRatingValues.map((value) => ({
        value,
        label: prettyPrintEnum(value),
      })),
    [],
  );

  const ruleOptions = useMemo(
    () =>
      transactionMonitoringRuleValues.map((value) => ({
        value,
        label: value,
        searchValues: [value],
      })),
    [],
  );

  const filterCount = [
    filterValue.statuses?.length ?? 0,
    filterValue.riskRatings?.length ?? 0,
    filterValue.rules?.length ?? 0,
  ].filter((count) => count > 0).length;

  const resetFilter = () => {
    onFilterChange({
      statuses: ["OPEN"],
      riskRatings: undefined,
      rules: undefined,
    });
  };

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={(nextOpen) => setOpen(nextOpen)}>
          <PopoverTrigger asChild>
            <Button
              leftIcon={<RiFilter3Line />}
              variant="outline"
              size="sm"
              className="h-8 w-fit text-primary"
            >
              Filter
              {filterCount > 0 ? (
                <span className="ml-2 text-info">({filterCount})</span>
              ) : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex h-[500px] w-[760px] p-0"
            align="start"
          >
            <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
              <div className="flex flex-col gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("status")}
                >
                  <RiPriceTagLine />
                  <div className="flex flex-1">Status</div>
                  {(filterValue.statuses?.length ?? 0) > 0 && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("risk")}
                >
                  <RiShieldLine />
                  <div className="flex flex-1">Risk rating</div>
                  {(filterValue.riskRatings?.length ?? 0) > 0 && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex justify-start gap-2 text-primary"
                  onClick={() => setPath("rule")}
                >
                  <RiListCheck />
                  <div className="flex flex-1">Rule</div>
                  {(filterValue.rules?.length ?? 0) > 0 && (
                    <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                  )}
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 bg-primary text-primary"
                onClick={resetFilter}
              >
                Reset all filters
              </Button>
            </div>
            <div className="flex-1">
              {path === "status" && (
                <ControlledFacetedFilterControl
                  options={statusOptions}
                  selectedValues={filterValue.statuses ?? []}
                  onChange={(statuses) =>
                    onFilterChange({
                      ...filterValue,
                      statuses:
                        statuses.length > 0
                          ? (statuses as typeof filterValue.statuses)
                          : undefined,
                    })
                  }
                />
              )}
              {path === "risk" && (
                <ControlledFacetedFilterControl
                  options={riskRatingOptions}
                  selectedValues={filterValue.riskRatings ?? []}
                  onChange={(riskRatings) =>
                    onFilterChange({
                      ...filterValue,
                      riskRatings:
                        riskRatings.length > 0
                          ? (riskRatings as typeof filterValue.riskRatings)
                          : undefined,
                    })
                  }
                />
              )}
              {path === "rule" && (
                <ControlledFacetedFilterControl
                  options={ruleOptions}
                  selectedValues={filterValue.rules ?? []}
                  onChange={(rules) =>
                    onFilterChange({
                      ...filterValue,
                      rules:
                        rules.length > 0
                          ? (rules as typeof filterValue.rules)
                          : undefined,
                    })
                  }
                />
              )}
            </div>
          </PopoverContent>
        </Popover>
        <ControlledFactedFilterSummary
          title="Status"
          options={statusOptions}
          selectedValues={filterValue.statuses ?? []}
          onClick={() => {
            setPath("status");
            setOpen(true);
          }}
        />
        <ControlledFactedFilterSummary
          title="Risk"
          options={riskRatingOptions}
          selectedValues={filterValue.riskRatings ?? []}
          onClick={() => {
            setPath("risk");
            setOpen(true);
          }}
        />
        <ControlledFactedFilterSummary
          title="Rule"
          options={ruleOptions}
          selectedValues={filterValue.rules ?? []}
          onClick={() => {
            setPath("rule");
            setOpen(true);
          }}
        />
        {filterCount > 0 && (
          <Button
            rightIcon={<X />}
            variant="ghost"
            size="sm"
            onClick={resetFilter}
          >
            Reset
          </Button>
        )}
      </div>
      <div className="text-secondary text-sm">
        {totalCount === 1 ? "1 finding" : `${totalCount} findings`}
      </div>
    </div>
  );
}
